/**
 * @module deep state (indexed db)
 *
 * For state that is bigger and more complex than what can be stored in memory.
 *
 * An adapter to the browser's IndexedDB offering convenient wrapper functions
 * for local db tasks to simplify the interface even further.
 *
 * @see https://dexie.org/docs/API-Reference
 */
import Dexie from 'dexie';
import { dbName } from '~/config/settings/deep_state.settings';

// -------------------------------------------------------------------- State --
// A singleton for the life of the session.
const db = new Dexie(dbName);

// ------------------------------------------------------------------ Helpers --
// Ensure incoming values meet expected shape.
const validateValue = (value) => {
  if (typeof value !== 'object' || typeof value.id !== 'number') {
    throw new Error('value must be an object and contain a numeric attribute `id`');
  }
};

// ---------------------------------------------------------------------- API --
// Initialize the database with stores and schemas.
// @see https://dexie.org/docs/Dexie/Dexie.version()
const init = async (version = 1, schema = {}) => {
  return db.version(version).stores(schema);
};

// Close the database connection.
// @see https://dexie.org/docs/Dexie/Dexie.close()
const close = async () => {
  return db.close();
};

// Clear a store's values.
// @see https://dexie.org/docs/Table/Table.clear()
const reset = async (storeName) => {
  return db[storeName].clear();
};

// Total number of values in a store.
// @see https://dexie.org/docs/Collection/Collection.count()
const count = async (storeName) => {
  return db[storeName].count();
};

// Add value, but reject if value with id already exists.
// @see https://dexie.org/docs/Table/Table.add()
const add = async (storeName, value = { id: 0 }) => {
  validateValue(value);

  return db[storeName].add(value, value.id);
};

// Add an array of values in bulk.
// @return the primaryKey of the last element added.
const addMany = async (storeName, values = []) => {
  return db[storeName].bulkAdd(values);
};

// Replace or insert value.
// @see https://dexie.org/docs/Table/Table.put()
const put = async (storeName, value = { id: 0 }) => {
  validateValue(value);

  return db[storeName].put(value, value.id);
};

// Replace or insert an array of values in bulk.
// @return the primaryKey of the last element added.
const putMany = async (storeName, values = []) => {
  return db[storeName].bulkPut(values);
};

// Merge with existing value or insert new value.
// @see https://dexie.org/docs/Transaction/Transaction
const merge = async (storeName, value = { id: 0 }) => {
  validateValue(value);

  return db.transaction('rw', db[storeName], async () => {
    const existingValue = (await db[storeName].get({ id: value.id })) || {};
    const mergedValue = {
      ...existingValue,
      ...value,
    };

    return db[storeName].put(mergedValue, value.id);
  });
};

// Get a single value by id.
// @see https://dexie.org/docs/Table/Table.get()
const get = async (storeName, id) => {
  return db[storeName].get(id);
};

// Get a single value using a set of key constraints.
const find = async (storeName, keyPaths) => {
  return db[storeName].get(keyPaths);
};

// Get multiple values in a certain order.
// @see https://dexie.org/docs/Table/Table.orderBy()
const getMany = async (storeName, limit = 10, orderBy = 'id', order = 'asc') => {
  if (order === 'desc') {
    return db[storeName]
      .orderBy(orderBy)
      .reverse() // this is the key difference in this case (not sure how to do this dynamically in a single command)
      .limit(limit)
      .toArray();
  }

  return db[storeName].orderBy(orderBy).limit(limit).toArray();
};

// Get a single page (subset) of a store.
// @see https://dexie.org/docs/Table/Table.offset()
const getPage = async (storeName, page = 1, per = 10, orderBy = 'id', order = 'asc') => {
  const start = page * per;

  if (order === 'desc') {
    return db[storeName]
      .orderBy(orderBy)
      .reverse() // this is the key difference in this case (not sure how to do this dynamically in a single command)
      .offset(start)
      .limit(per)
      .toArray();
  }

  return db[storeName].orderBy(orderBy).offset(start).limit(per).toArray();
};

// Remove a value from a store with matching id.
// @see https://dexie.org/docs/Collection/Collection.delete()
const remove = async (storeName, id) => {
  return db[storeName].where('id').equals(id).delete();
};

export { init, reset, close, count, add, addMany, put, putMany, merge, remove, find, get, getMany, getPage };
